import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  Address2ComponentValue,
  KybEntityCheckCard,
  KybEntityRegistryRow,
  KybEntityResult,
  KybEntitySummary,
  KybPerson,
  KybPersonRowState,
  PlacesDetailsFn,
  PlacesSearchResultFn,
  kybEntityAmlScreeningCheck,
  kybEntityAsKeyPeopleResult,
  kybEntityBlocklistCheck,
  kybEntityHasAcn,
  kybEntityRegistryRows,
} from '@portal-workspace/grow-shared-library';
import { DatePipe, NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MessageBoxComponent } from '../message-box/message-box.component';
import { TagBoxComponent } from '../message-box/tag-box.component';
import { AnnualRevenueComponent } from '../annual-revenue-component/annual-revenue.component';
import { CustomAddressComponent } from '../address-component/custom-address.component';
import { KybEntitySectionComponent, KybPersonDetailsRequired, KybPersonResolution } from '../kyb-key-people-component/kyb-entity-section.component';

/** Controls of the Key Company Details form; owned by the parent so the tab-level Save can read it. */
export interface KybKeyCompanyDetailsForm {
  annualRevenue: FormControl<number | null>;
  businessPhone: FormControl<string | null>;
  businessContactEmail: FormControl<string | null>;
  principalPlaceOfBusinessAddress: FormControl<Address2ComponentValue>;
}

@Component({
  selector: 'kyb-result-block',
  templateUrl: './kyb-result-block.component.html',
  styleUrls: ['./kyb-result-block.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    DatePipe,
    NgClass,
    MessageBoxComponent,
    TagBoxComponent,
    AnnualRevenueComponent,
    CustomAddressComponent,
    KybEntitySectionComponent,
  ],
})
export class KybResultBlockComponent {
  // Derived collections are computed on assignment rather than in getters: mat-table re-renders
  // whenever its dataSource reference changes, so returning a freshly filtered array from a getter
  // would re-render the register tables on every change-detection cycle.
  @Input({ required: true })
  set entity(value: KybEntitySummary) {
    this._entity = value;
    this.keyPeopleEntity = kybEntityAsKeyPeopleResult(value);
    this.checkCards = [kybEntityBlocklistCheck(value), kybEntityAmlScreeningCheck(value)];
    this.hasAcn = kybEntityHasAcn(value);
    const rows = kybEntityRegistryRows(value);
    // Without a KYB result nothing was ever looked up, so an empty row there means "never
    // collected" rather than "the registry holds nothing" - those rows are dropped and the
    // manual-entry badges above say the rest.
    this.visibleRegistryRows = value.hasKybResult ? rows : rows.filter(row => row.value !== null);
  }

  get entity(): KybEntitySummary {
    return this._entity;
  }

  private _entity!: KybEntitySummary;

  /** The same stakeholders as the key-people read model, so kyb-entity-section can render them. */
  keyPeopleEntity!: KybEntityResult;
  /** Blocklist then AML screening, rendered from one template since they read the same way. */
  checkCards: KybEntityCheckCard[] = [];
  visibleRegistryRows: KybEntityRegistryRow[] = [];
  /** ASIC registers are only returned for an ACN-registered entity; see kybEntityHasAcn. */
  hasAcn = false;

  @Input() nested = false;
  /** Supplied only for the primary borrowing entity; absent elsewhere so no panel renders. */
  @Input() keyCompanyDetailsForm: FormGroup<KybKeyCompanyDetailsForm> | null = null;
  @Input() placesSearchResultFn: PlacesSearchResultFn | null = null;
  @Input() placesDetailsFn: PlacesDetailsFn | null = null;
  /** False until the reviewer puts this entity into edit mode; the key-people ticks follow it. */
  @Input() editable = false;
  @Input() canRunWorkflow = false;
  @Input() workflowStarting = false;
  /**
   * Signer/guarantor rule messages for this entity's people, shown under their table so the rules
   * stay on screen while the ticks are made. Computed by the parent, which holds the ticks.
   */
  @Input() ruleErrors: string[] = [];
  @Input() ruleWarnings: string[] = [];
  @Output() keyPersonChange: EventEmitter<KybPersonRowState> = new EventEmitter<KybPersonRowState>();
  @Output() keyPersonDetailsRequired: EventEmitter<KybPersonDetailsRequired> = new EventEmitter<KybPersonDetailsRequired>();
  @Output() personResolutionUndo: EventEmitter<KybPerson> = new EventEmitter<KybPerson>();
  @Output() downloadReport: EventEmitter<KybEntitySummary> = new EventEmitter<KybEntitySummary>();
  @Output() runWorkflow: EventEmitter<KybEntitySummary> = new EventEmitter<KybEntitySummary>();
  @Input() resolutions: ReadonlyMap<number, KybPersonResolution> = new Map<number, KybPersonResolution>();
  @Input() stagedKeyPeople: ReadonlyMap<number, KybPersonRowState> = new Map<number, KybPersonRowState>();

  screeningDetailExpanded = false;

  @ViewChild(KybEntitySectionComponent) private entitySection?: KybEntitySectionComponent;

  /** Reverts a held-back tick on the people table (details dialog cancelled). */
  revertTick(person: KybPerson) {
    this.entitySection?.revertTick(person);
  }

  applyConfirmedTick(
    person: KybPerson,
    isSelected: boolean,
    isGuarantor: boolean,
    isSigner: boolean,
  ): void {
    this.entitySection?.applyConfirmedTick(person, isSelected, isGuarantor, isSigner);
  }

  livePerson(stakeholderId: number): KybPerson | null {
    return this.keyPeopleEntity?.people.find(person => person.stakeholderId === stakeholderId) ?? null;
  }

  get showKybResult(): boolean {
    return this.entity.hasKybResult;
  }

  workflowButtonLabel(): string {
    return this.entity.runStatus ? 'Re-run workflow' : 'Run workflow';
  }

  showWorkflowAction(): boolean {
    return this.canRunWorkflow && !this.entity.hasKybResult;
  }

  workflowCooldownActive(): boolean {
    const canRunAt = this.workflowCanRunAt();
    return canRunAt !== null && canRunAt > Date.now();
  }

  workflowButtonTooltip(): string {
    if (this.workflowStarting) {
      return 'Starting a new FrankieOne KYB workflow';
    }
    const canRunAt = this.workflowCanRunAt();
    if (canRunAt !== null && canRunAt > Date.now()) {
      return `Another workflow can be started after ${new Date(canRunAt).toLocaleString()}`;
    }
    return this.entity.runStatus
      ? 'Start another FrankieOne KYB workflow because no result has been received yet'
      : 'Start a FrankieOne KYB workflow';
  }

  private workflowCanRunAt(): number | null {
    if (!this.entity.workflowCanRunAt) {
      return null;
    }
    const value = new Date(this.entity.workflowCanRunAt).getTime();
    return Number.isNaN(value) ? null : value;
  }

  /**
   * One line per thing a reviewer should not have to scroll to find: failed checks, reported
   * issues, and signer/guarantor rules the current ticks break. The details stay in their sections
   * below; this strip only points at them. Not precomputed like the collections above: it reads
   * ruleErrors, which change on every tick.
   */
  get attentionItems(): string[] {
    const items: string[] = [];
    for (const check of this.checkCards) {
      if (check.passed === false) {
        items.push(`${check.title}: ${check.outcome}`);
      }
    }
    if (this.entity.issueCount > 0) {
      const plural = this.entity.issueCount === 1 ? 'issue' : 'issues';
      items.push(`${this.entity.issueCount} ${plural} reported — listed under Issues below`);
    }
    if (this.ruleErrors.length) {
      items.push('Signer/guarantor rules are not met — see the people table below');
    }
    return items;
  }

  get annualRevenueControl(): FormControl<number | null> | null {
    return this.keyCompanyDetailsForm?.controls.annualRevenue ?? null;
  }

  get businessPhoneControl(): FormControl<string | null> | null {
    return this.keyCompanyDetailsForm?.controls.businessPhone ?? null;
  }

  get businessContactEmailControl(): FormControl<string | null> | null {
    return this.keyCompanyDetailsForm?.controls.businessContactEmail ?? null;
  }

  get principalPlaceControl(): FormControl<Address2ComponentValue> | null {
    return this.keyCompanyDetailsForm?.controls.principalPlaceOfBusinessAddress ?? null;
  }

  checkIcon(passed: boolean | null): string {
    if (passed === null) {
      return 'mdi-help-circle-outline';
    }
    return passed ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline';
  }

  /** Shared by the card's left edge and its icon, so both read the same outcome. */
  checkStateClass(passed: boolean | null): string {
    if (passed === null) {
      return 'unknown';
    }
    return passed ? 'pass' : 'fail';
  }

  statusClass(positive: boolean | null): string {
    if (positive === null) {
      return 'unknown';
    }
    return positive ? 'pass' : 'fail';
  }

  toggleScreeningDetail() {
    this.screeningDetailExpanded = !this.screeningDetailExpanded;
  }

  displayValue(value: string | number | null | undefined): string {
    if (value == null || value === '') {
      return '—';
    }
    return String(value);
  }
}
